import { HoldingSession } from "../api/types"
import { OmniaConfig } from "../config"
import { getTestSettings } from "../session/settings/test"
import { ConflictMessage, HelpLink, IOmniaShell } from "../shell"
import { IOmniaMaintenance, IOmniaMaintenanceService, IOmniaSession, OmniaAppId } from "../types"
import { RoleId } from "../user"
import { reportFeedback } from "../usersnap"
import { defineDirective } from "../utils/angular"
import { getProperty } from "../utils/objects"
import { isDate, isFunction } from "../utils/utils"

/** @internal */
export class MainViewCtrl {

    static create(): ng.IDirective {
        return defineDirective(MainViewCtrl, "shared/main/main.html")
    }

    /** This is used only through a watch expression - not explicitely referenced from the class, but most not be deleted  */
    private readonly apiState = this.session.apiState


    private testSettings = getTestSettings(this.session)

    private _helpLinks: { [key: string | number]: HelpLink[] }

    private _refreshing = false

    public conflicts = false
    public showConflictsPopup = false
    public conflictMessage: ConflictMessage
    public conflictingSessions = [] as HoldingSession[]

    public years: number[]

    public showMaintenancePopup = false

    static $inject = ["$window", "$scope", "$uibModal", "$timeout", "OmniaSession", "OmniaShell", "OmniaMaintenance", "omniaConfig"]

    constructor(
        private $window: ng.IWindowService,
        private $scope: ng.IScope,
        private $uibModal: any,
        private $timeout: ng.ITimeoutService,
        public readonly session: IOmniaSession,
        public readonly shell: IOmniaShell,
        private maintenanceSvc: IOmniaMaintenanceService,
        private readonly config: OmniaConfig,
        readonly production = session.production,
        private readonly sessionState = session.sessionState) {

        setupFilter($scope, $window, () => !!this.filter)

    }

    $onInit() {

        const { $scope, shell } = this

        $scope.$watch("vm.session.currentUserRole", () => this.setupHelpLinks())

        $scope.$watchGroup(["vm.apiState.conflicts", "vm.shell.conflict"], () => this.setupConflicts())

        $scope.$watchGroup(["vm.session.holdingId", "vm.session.yearId", "vm.session.holdingYears"], () => this.setupYears())

        $scope.$watch("vm.nextMaintenance", (a: IOmniaMaintenance, b: IOmniaMaintenance) => {

            if (shouldShowInitMaintenancePopup(a, b)) {
                this.showMaintenancePopup = true
            }

        })

        this.runTestFunction($scope, shell)

    }

    getCssClass() {

        const { ready, autoscroll, config } = this
        const v2024 = config.login === "2024"

        return `${ready ? "" : "wait"} ${autoscroll ? "scroll" : ""} ${v2024 ? "v2024" : "default"}`

    }

    setupYears() {

        const { session } = this
        const years = session.holdingYears

        this.years = (years || [])
            .map(y => y.YearId)
            .sort((a, b) => b - a)

    }

    getTemplateKey() {

        const { session, ready, maintenance, autoSignup, loggedIn } = this
        const { shouldResetPassword, loginLinkError, passwordResetRequested, restrictedLogin, termsAndConditions } = session

        if (maintenance) {
            return "maintenance/panel"
        }

        if (!ready) {
            return "load"
        }

        if (loginLinkError) {
            return "link"
        }

        if (autoSignup) {
            return "signup"
        }

        if (!loggedIn) {

            if (shouldResetPassword?.reset) {
                return "resetpwd1"
            }

            if (passwordResetRequested) {
                return "resetpwd2"
            }

            return "login"

        }

        if (loggedIn && restrictedLogin) {
            return "login"
        }

        if (loggedIn && (termsAndConditions?.mustAccept)) {
            return "login"
        }

        return "app"

    }

    getTemplate() {
        const key = this.getTemplateKey()
        return "shared/main/templates/" + key + ".html"
    }

    showCoverImage() {
        const { config } = this
        if (config.login !== "2024") return false
        const key = this.getTemplateKey()
        return key !== "app"
    }

    get loggedIn() {
        const { session: { currentUser, holding } } = this
        return Boolean(currentUser && holding)
    }

    get holdingName() {
        const { session } = this
        return session?.holding?.TradingName
    }

    get year() {
        return this.session?.yearId
    }

    get readOnly() {
        const { session } = this
        return session.isReadOnly()
    }

    get reloading() {
        return this.session.reloading
    }

    get autoSignup() {
        return this.session.autoSignup
    }

    get disableMenu() {
        return this.shell.disableMenu
    }

    get helpLinks() {

        const module = this.shell.getCurrentModule()

        if (module) {
            const id = module.helpSection ?? module.moduleId
            return this._helpLinks[id]
        }

    }

    get ready(): boolean {

        const { session, loggedIn } = this

        if (!loggedIn) {
            return session.isInitialized
        }

        const { currentUser, holding, yearId } = session
        return holding && yearId && Boolean(currentUser)

    }

    get autoscroll() {
        return this.getTemplateKey() != "app"
    }

    get appBrandName() {
        return this.config?.brand?.name
    }

    get filter() {
        const mod = this.shell.getCurrentModule()
        return mod?.getCurrentFilter()
    }

    getNextMaintenanceText() {
        return this.nextMaintenance?.message
    }

    get maintenance(): boolean {
        return !!this.maintenanceSvc.current
    }

    get nextMaintenance() {
        return this.maintenanceSvc.next
    }

    get maintenanceEndDate() {
        return this.maintenanceSvc.current?.endDate
    }

    get unauthorized() {
        return this.sessionState.unauthorized
    }

    get unavailable() {
        return this.sessionState.unavailable
    }

    reload() {
        this.$window.location.reload()
    }

    get refreshing() {
        return this._refreshing
    }

    showRefresh() {
        return this.testSettings.showRefreshButton
    }

    canRefresh() {
        return this.shell.canRefresh()
    }

    refresh() {

        this._refreshing = true
        this.$scope.$applyAsync()
        this.session.updateSession().then(() => this.shell.refresh(false))
        this.$timeout(1000).then(() => this._refreshing = false)

    }

    selectHolding() {
        this.shell.showComponentAsDialog("omnia-main-context-dialog", null, { backdrop: "static" })
    }

    selectYear(year: number) {

        if (this.session.selectYear(year)) {
            this.shell.startBlockUI()
            const done = () => this.shell.stopBlockUI()
            this.shell.refresh(true).then(done, done)
        }

    }

    openHelpLink(link: HelpLink) {
        var scope = this.$scope.$new(true)
        scope["link"] = link

        this.$uibModal.open({
            template: "<omnia-help-dialog />",
            scope: scope
        })
    }

    getAppLogo() {
        const name = this.config?.brand?.images ?? "omnia"
        const b = this.config?.appId !== OmniaAppId.Omnia
        return b
            ? `brand/${name}/tagline-200-dark.png`
            : `brand/${name}/tagline-dark.svg`
    }

    getHelpIcon(link: HelpLink) {
        return link.link.indexOf('help') === -1 ? "icon-video" : "icon-document"
    }

    isHelpDocument(link: HelpLink) {
        return link.link.indexOf('help') >= 0
    }

    getHelpTitle(link: HelpLink) {
        return link.title.replace("(@brand)", this.appBrandName)
    }

    reportFeedback() {
        reportFeedback(this.session)
    }


    get showMaintenanceIcon() {
        return !!this.maintenanceSvc.next
    }

    setupHelpLinks() {

        const { session, config } = this

        const links = this._helpLinks = config.support.getHelpLinks()

        if (session.currentUserRole == RoleId.Access) {

            for (const id in links) {
                links[id] = links[id].filter(l => l.accessRole !== false)
            }

        }

    }

    private setupConflicts() {

        this.conflictMessage = this.shell.conflict
        const conflicts = this.conflictingSessions = this.apiState.conflicts || []

        this.conflicts = this.showConflictsPopup = (!!this.conflictMessage) || (conflicts.length > 0)

    }

    public closeConflictsPopup() {
        this.showConflictsPopup = false
    }

    private runTestFunction($scope: ng.IScope, shell: IOmniaShell) {

        const session = shell.getSession()
        const testFunctionName = session.getSeachParam("startupTestFunction")

        if (!testFunctionName) {
            return
        }

        const testFunction = getProperty(window, testFunctionName)

        if (isFunction(testFunction)) {

            let started = false

            $scope.$watch(() => this.getTemplateKey(), key => {
                if ((key === "app") && (!started)) {
                    started = true
                    testFunction(shell)
                }
            })

        }

    }

}

function setupFilter($scope: ng.IScope, $window: ng.IWindowService, isFilterEnabled: () => boolean) {

    const filterPopup = { open: false }
    $scope["popupContext"] = filterPopup

    $window.document.addEventListener("keydown", e => {

        if (e.ctrlKey && (e.key == "f") && isFilterEnabled()) {
            e.preventDefault()
            filterPopup.open = true
            $scope.$applyAsync()
        }

    })

}

function shouldShowInitMaintenancePopup(after: IOmniaMaintenance, before: IOmniaMaintenance) {

    if (after && (!before) && isDate(after.startDate)) {

        const diff = after.startDate.valueOf() - Date.now()

        if (diff < (1 * 24 * 60 * 60 * 1000)) {
            return true
        }

    }

}


